<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import InteractiveHistogram from '@/components/Charts/InteractiveHistogram.vue'
import LogTypesSidebar from '@/components/EventSearch/LogTypesSidebar.vue'
import ZeekLogsTable from '@/components/EventSearch/ZeekLogsTable.vue'
import { makeServerTermsProvider } from '@/components/EventSearch/columnStats'

// Dynamic loading messages - ordered progression from fun to helpful hints
const loadingMessages = [
  // Initial fun messages
  "Searching the void...",
  "Crunching numbers...",
  "Herding packets...",
  "Interrogating logs...",
  // Start hinting at volume
  "That's a lot of data...",
  "Still going...",
  "Processing many results...",
  // Suggest narrowing query
  "This query found a lot of results...",
  "Try a shorter time range for faster results",
  "Adding filters can speed things up",
  "Tip: Narrower queries are faster",
  "Consider adding more specific terms",
  // Patience messages
  "Still working on it...",
  "Almost there, probably...",
  "Your patience is appreciated",
]

const currentMessageIndex = ref(0)
let messageInterval = null

function startMessageRotation() {
  if (messageInterval) return
  currentMessageIndex.value = 0 // Always start from the beginning
  messageInterval = setInterval(() => {
    // Stop at the last message instead of cycling
    if (currentMessageIndex.value < loadingMessages.length - 1) {
      currentMessageIndex.value++
    }
  }, 4000)
}

function stopMessageRotation() {
  if (messageInterval) {
    clearInterval(messageInterval)
    messageInterval = null
  }
}

const currentLoadingMessage = computed(() => loadingMessages[currentMessageIndex.value])

onUnmounted(() => {
  stopMessageRotation()
})

const props = defineProps({
  query: {
    type: String,
    default: ''
  },
  baseQuery: {
    type: String,
    default: ''
  },
  histogram: {
    type: Array,
    default: () => []
  },
  logTypeDistribution: {
    type: Array,
    default: () => []
  },
  sourceLogTypeDistribution: {
    type: Array,
    default: () => []
  },
  displayedLogType: {
    type: String,
    default: null
  },
  activeSource: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  },
  forbidden: {
    type: Boolean,
    default: false
  },
  tableResults: {
    type: Array,
    default: () => []
  },
  tableTotal: {
    type: Number,
    default: 0
  },
  tableCurrentPage: {
    type: Number,
    default: 0
  },
  tableLoading: {
    type: Boolean,
    default: false
  },
  tableMoreResults: {
    type: Boolean,
    default: false
  },
  pageSize: {
    type: Number,
    default: 50
  },
  fieldMappings: {
    type: Object,
    default: () => ({})
  },
  isFieldEnabled: {
    type: Function,
    default: () => () => true
  },
  toggleField: {
    type: Function,
    default: () => () => {}
  },
  getFieldOrder: {
    type: Function,
    default: () => () => []
  },
  setFieldOrder: {
    type: Function,
    default: () => () => {}
  },
  getOrderedEnabledFields: {
    type: Function,
    default: () => () => []
  },
  resetLogType: {
    type: Function,
    default: () => () => {}
  },
  hasCustomSettings: {
    type: Function,
    default: () => () => false
  },
  tableSort: {
    type: Array,
    default: () => []
  },
  sources: {
    type: Array,
    default: () => []
  }
})

// Column header stats come from the server here — the counts describe the whole
// index, not the page of rows on screen.
const statsProvider = computed(() => makeServerTermsProvider({
  query: props.query,
  logTypes: props.displayedLogType ? [props.displayedLogType] : [],
  sources: props.sources
}))

// Compute enabled fields for the current log type (ordered)
// Now returns FieldInfo objects with name and type
const enabledFields = computed(() => {
  if (!props.displayedLogType || !props.fieldMappings[props.displayedLogType]) {
    return null // null means show all (fallback to config)
  }
  const allFields = props.fieldMappings[props.displayedLogType]
  return props.getOrderedEnabledFields(props.displayedLogType, allFields)
})

// Compute field types map for the current log type (for quick lookup)
// Returns { fieldName: { type: string, hasKeyword: boolean } }
const fieldTypes = computed(() => {
  if (!props.displayedLogType || !props.fieldMappings[props.displayedLogType]) {
    return {}
  }
  const fields = props.fieldMappings[props.displayedLogType]
  const typeMap = {}
  for (const field of fields) {
    // Handle both old format (string) and new format (object with name/type/has_keyword)
    if (typeof field === 'object' && field.name) {
      typeMap[field.name] = {
        type: field.type || 'unknown',
        hasKeyword: field.has_keyword || false
      }
    } else if (typeof field === 'string') {
      typeMap[field] = { type: 'unknown', hasKeyword: false }
    }
  }
  return typeMap
})

// Watch loading state to start/stop message rotation
watch(() => props.loading, (isLoading) => {
  if (isLoading && props.logTypeDistribution.length === 0) {
    startMessageRotation()
  } else {
    stopMessageRotation()
  }
}, { immediate: true })

const emit = defineEmits([
  'update:time-filter',
  'select-log-type',
  'select-source',
  'select-source-log-type',
  'table-next-page',
  'table-prev-page',
  'apply-filter',
  'sort-change',
  'add-filter',
  'update:page-size',
  'navigate-aggregations'
])

// Handle histogram bar click (timestamp and interval are in milliseconds)
function handleHistogramBarClick({ timestamp, interval }) {
  emit('update:time-filter', {
    start: timestamp,
    end: timestamp + interval
  })
}

// Handle histogram brush select (times are in milliseconds)
function handleHistogramBrushSelect({ startTime, endTime }) {
  emit('update:time-filter', {
    start: startTime,
    end: endTime
  })
}
</script>

<template>
  <div class="default-view">
    <!-- Forbidden State (tier upgrade required) -->
    <div v-if="forbidden" class="forbidden-state">
      <div class="state-box forbidden-box">
        <font-awesome-icon :icon="['fas', 'lock']" class="forbidden-icon" />
        <span class="state-message">Event Search requires an Elite or Enterprise subscription</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <div class="state-box error-box">
        <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="error-icon" />
        <span class="state-message">{{ error }}</span>
      </div>
    </div>

    <!-- Loading State (initial) -->
    <div v-else-if="loading && logTypeDistribution.length === 0" class="loading-state">
      <div class="state-box">
        <div class="spinner"></div>
        <span class="state-message">{{ currentLoadingMessage }}</span>
      </div>
    </div>

    <!-- Empty State (no query executed yet) -->
    <div v-else-if="!loading && logTypeDistribution.length === 0 && !baseQuery" class="empty-state">
      <div class="state-box">
        <span class="state-message">Execute a query to see results here</span>
        <span class="state-hint">Use the search bar above to search your network events</span>
      </div>
    </div>

    <!-- Empty State (no results) -->
    <div v-else-if="!loading && logTypeDistribution.length === 0" class="empty-state">
      <div class="state-box">
        <span class="state-message">No results found</span>
        <span class="state-hint">Try adjusting your query or time range</span>
      </div>
    </div>

    <!-- Histogram Section -->
    <div v-if="logTypeDistribution.length > 0" class="histogram-section">
      <InteractiveHistogram
        :data="histogram"
        :highlighted-log-type="displayedLogType"
        @bar-click="handleHistogramBarClick"
        @brush-select="handleHistogramBrushSelect"
        height="120px"
      />
    </div>

    <!-- Results Section with Sidebar -->
    <div v-if="logTypeDistribution.length > 0" class="results-with-sidebar">
      <!-- Log Types Sidebar -->
      <LogTypesSidebar
        :log-type-distribution="logTypeDistribution"
        :source-log-type-distribution="sourceLogTypeDistribution"
        :active-log-type="displayedLogType"
        :active-source="activeSource"
        :loading="loading"
        @select="$emit('select-log-type', $event)"
        @select-source="$emit('select-source', $event)"
        @select-source-log-type="$emit('select-source-log-type', $event)"
      />

      <!-- Results Table -->
      <div class="results-section">
        <ZeekLogsTable
          :results="tableResults"
          :log-type="displayedLogType"
          :loading="tableLoading"
          :total="tableTotal"
          :current-page="tableCurrentPage"
          :page-size="pageSize"
          :more-results="tableMoreResults"
          :enabled-fields="enabledFields"
          :field-mappings="fieldMappings"
          :sort="tableSort"
          :field-types="fieldTypes"
          :base-query="baseQuery"
          :column-stats="statsProvider"
          selectable
          cell-filters
          context-menu
          @next-page="$emit('table-next-page')"
          @prev-page="$emit('table-prev-page')"
          @apply-filter="(queue, operator) => $emit('apply-filter', queue, operator)"
          @sort-change="(field, type) => $emit('sort-change', field, type)"
          @add-filter="(filterData) => $emit('add-filter', filterData)"
          @update:page-size="(size) => $emit('update:page-size', size)"
          @navigate-aggregations="(field) => $emit('navigate-aggregations', field)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.default-view {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

/* Forbidden State */
.forbidden-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(21, 26, 34, 0.7);
  border-radius: var(--radius-lg);
}

.forbidden-box {
  border-color: rgba(107, 114, 128, 0.4);
}

.forbidden-icon {
  font-size: 2rem;
  color: var(--text-faint);
  opacity: 0.6;
}

.upgrade-link {
  color: var(--accent);
  font-size: 0.9rem;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.upgrade-link:hover {
  color: var(--accent-text);
}

/* Error State */
.error-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(21, 26, 34, 0.7);
  border-radius: var(--radius-lg);
}

.error-box {
  border-color: rgba(158, 74, 74, 0.4);
}

.error-icon {
  font-size: 2rem;
  color: var(--color-error);
  opacity: 0.8;
}

/* Histogram Section */
.histogram-section {
  margin-bottom: 1rem;
  flex-shrink: 0;
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  padding: 0rem;
  padding-top: .5rem;
}

/* Results with Sidebar Layout */
.results-with-sidebar {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 1rem;
}

/* Results Section */
.results-section {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
}

/* Loading State */
.loading-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(21, 26, 34, 0.7);
  border-radius: var(--radius-lg);
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 3rem;
  background: var(--surface-page);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.state-message {
  color: var(--text-primary);
  font-size: 0.95rem;
  text-align: center;
}

.state-hint {
  color: var(--text-faint);
  font-size: 0.8rem;
  text-align: center;
}

/* Empty State */
.empty-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(21, 26, 34, 0.7);
  border-radius: var(--radius-lg);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--text-disabled);
  border-top: 3px solid var(--accent);
  border-radius: var(--radius-pill);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
